import React, { useEffect, useMemo, useState } from 'react';
import {
  AdminStockHistoryCreateRequest,
  AdminStockHistoryDto,
  AdminUserDto,
  adminApi,
  isUnauthorized,
} from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminStockHistoryScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

type SortKey = keyof AdminStockHistoryDto | 'username';

const AdminStockHistoryScreen: React.FC<AdminStockHistoryScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const colors = useMemo(
    () => ({
      card: theme.cardBg,
      border: theme.border,
      text: theme.text,
      subText: theme.subText,
      hover: theme.hoverBg,
      inputBg: theme.inputBg,
      primary: theme.primary,
      danger: theme.danger,
      success: theme.success,
      radius: theme.radius,
    }),
    [theme]
  );

  const [items, setItems] = useState<AdminStockHistoryDto[] | null>(null);
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const [gridSearch, setGridSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'id', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [criteriaCollapsed, setCriteriaCollapsed] = useState(false);
  const [criteriaSize, setCriteriaSize] = useState<'compact' | 'normal' | 'expanded'>('normal');

  const [criteriaDraft, setCriteriaDraft] = useState({
    createdFrom: '',
    createdTo: '',
    username: '',
    stockName: '',
  });
  const [criteriaApplied, setCriteriaApplied] = useState(criteriaDraft);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [newItem, setNewItem] = useState<AdminStockHistoryCreateRequest>({
    userId: 0,
    stockName: '',
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 700,
  };

  const buttonStyle = (variant: 'primary' | 'neutral' | 'danger' | 'success', disabled?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: 34,
      padding: '0 12px',
      borderRadius: colors.radius,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
      color: colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13,
      fontWeight: 800,
      opacity: disabled ? 0.6 : 1,
    };

    if (variant === 'primary') return { ...base, border: 'none', backgroundColor: colors.primary, color: '#fff' };
    if (variant === 'danger') return { ...base, border: 'none', backgroundColor: colors.danger, color: '#fff' };
    if (variant === 'success') return { ...base, border: 'none', backgroundColor: colors.success, color: '#fff' };
    return base;
  };

  const normalize = (v: unknown): string => (v ?? '').toString().trim().toLowerCase();

  const parseDate = (value: string): Date | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const refresh = async (opts?: { clearSelection?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const [history, usersList] = await Promise.all([adminApi.listStockHistory(token), adminApi.listUsers(token)]);
      setItems(history);
      setUsers(usersList);
      if (opts?.clearSelection) setSelectedIds(new Set());
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading stock history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh({ clearSelection: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const userLookup = useMemo(() => {
    const map = new Map<number, string>();
    (users ?? []).forEach((u) => {
      map.set(u.id, u.username);
    });
    return map;
  }, [users]);

  const criteriaLayout = useMemo(() => {
    if (criteriaSize === 'compact') return { minColWidth: 200, gap: 8, padding: 10 };
    if (criteriaSize === 'expanded') return { minColWidth: 260, gap: 12, padding: 14 };
    return { minColWidth: 220, gap: 10, padding: 12 };
  }, [criteriaSize]);

  const criteriaGridMinWidth = useMemo(() => {
    return 5 * criteriaLayout.minColWidth + 4 * criteriaLayout.gap;
  }, [criteriaLayout.gap, criteriaLayout.minColWidth]);

  const applyCriteria = () => {
    setCriteriaApplied(criteriaDraft);
    setPage(1);
    setSelectedIds(new Set());
  };

  const clearCriteria = () => {
    const cleared = { createdFrom: '', createdTo: '', username: '', stockName: '' };
    setCriteriaDraft(cleared);
    setCriteriaApplied(cleared);
    setGridSearch('');
    setPage(1);
    setSelectedIds(new Set());
  };

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key: SortKey): string => {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' ▲' : ' ▼';
  };

  const filteredItems = useMemo(() => {
    const list = items ?? [];
    const q = normalize(gridSearch);

    const createdFrom = parseDate(criteriaApplied.createdFrom);
    const createdTo = parseDate(criteriaApplied.createdTo);

    const usernameFilter = criteriaApplied.username.trim();
    const stockNameFilter = criteriaApplied.stockName.trim();

    const result = list.filter((x) => {
      if (usernameFilter.length > 0) {
        const resolvedUsername = (x.username ?? userLookup.get(x.userId) ?? '').toString();
        if (!normalize(resolvedUsername).includes(normalize(usernameFilter))) return false;
      }
      if (stockNameFilter.length > 0) {
        if (!normalize(x.stockName).includes(normalize(stockNameFilter))) return false;
      }

      if (createdFrom || createdTo) {
        const created = new Date(x.createdAtUtc);
        if (!Number.isNaN(created.getTime())) {
          if (createdFrom && created < createdFrom) return false;
          if (createdTo) {
            const createdToEnd = new Date(createdTo);
            createdToEnd.setHours(23, 59, 59, 999);
            if (created > createdToEnd) return false;
          }
        }
      }

      if (q.length > 0) {
        const haystack = [x.id, x.userId, x.username, x.stockName, x.createdAtUtc].map((v) => (v ?? '').toString().toLowerCase()).join(' | ');
        if (!haystack.includes(q)) return false;
      }
      return true;
    });

    const compare = (a: AdminStockHistoryDto, b: AdminStockHistoryDto): number => {
      const dir = sort.dir === 'asc' ? 1 : -1;

      const getVal = (x: AdminStockHistoryDto): string | number => {
        if (sort.key === 'username') return (x.username ?? '').toString().toLowerCase();
        const v = (x as any)[sort.key];
        if (typeof v === 'number') return v;
        if (sort.key === 'createdAtUtc') {
          const d = new Date(v as string);
          return Number.isNaN(d.getTime()) ? 0 : d.getTime();
        }
        return (v ?? '').toString().toLowerCase();
      };

      const av = getVal(a);
      const bv = getVal(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return av.toString().localeCompare(bv.toString()) * dir;
    };

    return result.slice().sort(compare);
  }, [criteriaApplied.createdFrom, criteriaApplied.createdTo, criteriaApplied.stockName, criteriaApplied.username, gridSearch, items, sort.dir, sort.key, userLookup]);

  const total = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const pageItems = useMemo(() => {
    const start = (pageSafe - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, pageSafe, pageSize]);

  const formatDateTime = (value?: string | null): string => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await adminApi.deleteStockHistory(token, id);
      }
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed deleting items');
    } finally {
      setLoading(false);
    }
  };

  const submitAdd = async () => {
    setAddError(null);

    const userId = Number(newItem.userId);
    const username = (newItem.username ?? '').toString().trim();
    const category = (newItem.category ?? '').toString().trim();
    const stockName = newItem.stockName.trim();

    const hasUserId = Number.isFinite(userId) && userId > 0;
    const hasUsername = username.length > 0;
    const hasCategory = category.length > 0;

    if ((!hasUserId && !hasUsername && !hasCategory) || stockName.length === 0) {
      setAddError('Username (or UserId) and Stock name are required');
      return;
    }

    setIsAdding(true);
    try {
      await adminApi.createStockHistory(token, {
        userId: hasUserId ? userId : 0,
        username: hasUsername ? username : undefined,
        category: hasCategory ? category : undefined,
        stockName,
      });
      setIsAddOpen(false);
      setNewItem({ userId: 0, stockName: '', username: '', category: '' });
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setAddError(e instanceof Error ? e.message : 'Failed creating item');
    } finally {
      setIsAdding(false);
    }
  };

  const userOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => `${u.id} - ${u.username}`)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const usernameOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.username ?? '').toString().trim())
      .filter((u) => u.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Toolbar */}
      <div
        style={{
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.radius,
          padding: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" style={buttonStyle('primary', loading)} disabled={loading} onClick={() => refresh({ clearSelection: true })}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button type="button" style={buttonStyle('success', loading)} disabled={loading} onClick={() => setIsAddOpen(true)}>
            Add
          </button>
          <button type="button" style={buttonStyle('danger', loading || selectedIds.size === 0)} disabled={loading || selectedIds.size === 0} onClick={deleteSelected}>
            Delete ({selectedIds.size})
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: colors.subText }}>Rows</div>
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value) || 10)} style={inputStyle}>
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Search criteria */}
      <div
        style={{
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.radius,
          padding: criteriaLayout.padding,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: colors.text }}>Search criteria</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: colors.subText }}>Size</div>
            <select value={criteriaSize} onChange={(e) => setCriteriaSize(e.target.value as any)} style={inputStyle}>
              <option value="compact">Compact</option>
              <option value="normal">Normal</option>
              <option value="expanded">Expanded</option>
            </select>

            <button type="button" style={buttonStyle('neutral')} onClick={() => setCriteriaCollapsed((v) => !v)}>
              {criteriaCollapsed ? 'Expand' : 'Collapse'}
            </button>
          </div>
        </div>

        {!criteriaCollapsed ? (
          <>
            <div style={{ marginTop: 10, overflowX: 'auto' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(5, minmax(${criteriaLayout.minColWidth}px, 1fr))`,
                  gap: criteriaLayout.gap,
                  minWidth: criteriaGridMinWidth,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Created from</div>
                  <input type="date" value={criteriaDraft.createdFrom} onChange={(e) => setCriteriaDraft((p) => ({ ...p, createdFrom: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Created to</div>
                  <input type="date" value={criteriaDraft.createdTo} onChange={(e) => setCriteriaDraft((p) => ({ ...p, createdTo: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Username</div>
                  <input
                    value={criteriaDraft.username}
                    onChange={(e) => setCriteriaDraft((p) => ({ ...p, username: e.target.value }))}
                    placeholder="Select or type..."
                    list="admin-stock-history-username-options"
                    style={inputStyle}
                  />
                  <datalist id="admin-stock-history-username-options">
                    {usernameOptions.map((u) => (
                      <option key={u} value={u} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Stock name</div>
                  <input value={criteriaDraft.stockName} onChange={(e) => setCriteriaDraft((p) => ({ ...p, stockName: e.target.value }))} placeholder="..." style={inputStyle} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.subText, marginBottom: 6 }}>Table search</div>
                  <input value={gridSearch} onChange={(e) => setGridSearch(e.target.value)} placeholder="..." style={inputStyle} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
              <button type="button" style={buttonStyle('primary')} onClick={applyCriteria}>
                Search
              </button>
              <button type="button" style={buttonStyle('neutral')} onClick={clearCriteria}>
                Clear
              </button>
            </div>
          </>
        ) : null}
      </div>

      {/* Grid */}
      <div
        style={{
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.radius,
          overflow: 'hidden',
        }}
      >
        {error ? <div style={{ padding: 12, color: isDarkMode ? '#FCA5A5' : '#B91C1C', fontWeight: 900 }}>{error}</div> : null}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1200 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={{ textAlign: 'center', padding: '10px 12px', color: colors.subText, fontSize: 12, width: 48 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all rows on page"
                    checked={pageItems.length > 0 && pageItems.every((x) => selectedIds.has(x.id))}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (checked) pageItems.forEach((x) => next.add(x.id));
                        else pageItems.forEach((x) => next.delete(x.id));
                        return next;
                      });
                    }}
                    style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                  />
                </th>
                <th onClick={() => toggleSort('id')} style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, cursor: 'pointer' }}>
                  Id{sortIndicator('id')}
                </th>
                <th onClick={() => toggleSort('userId')} style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, cursor: 'pointer' }}>
                  UserId{sortIndicator('userId')}
                </th>
                <th onClick={() => toggleSort('username')} style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, cursor: 'pointer' }}>
                  Username{sortIndicator('username')}
                </th>
                <th onClick={() => toggleSort('stockName')} style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, cursor: 'pointer' }}>
                  Stock name{sortIndicator('stockName')}
                </th>
                <th onClick={() => toggleSort('createdAtUtc')} style={{ textAlign: 'left', padding: '10px 12px', color: colors.subText, fontSize: 12, cursor: 'pointer' }}>
                  Created{sortIndicator('createdAtUtc')}
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((x) => {
                const isSelected = selectedIds.has(x.id);
                return (
                  <tr
                    key={x.id}
                    style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: isSelected ? (isDarkMode ? colors.hover : '#EEF2FF') : 'transparent' }}
                    onMouseEnter={(e) => {
                      if (isSelected) return;
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = colors.hover;
                    }}
                    onMouseLeave={(e) => {
                      if (isSelected) return;
                      (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
                    }}
                    onClick={() => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(x.id)) next.delete(x.id);
                        else next.add(x.id);
                        return next;
                      });
                    }}
                  >
                    <td style={{ padding: '10px 12px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(x.id);
                            else next.delete(x.id);
                            return next;
                          });
                        }}
                        style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 900 }}>{x.id}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 800 }}>{x.userId}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 800 }}>{x.username ?? userLookup.get(x.userId) ?? ''}</td>
                    <td style={{ padding: '10px 12px', color: colors.text, fontSize: 13, fontWeight: 900 }}>{x.stockName}</td>
                    <td style={{ padding: '10px 12px', color: colors.subText, fontSize: 13, fontWeight: 800 }}>{formatDateTime(x.createdAtUtc)}</td>
                  </tr>
                );
              })}

              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 16, color: colors.subText, fontWeight: 800 }}>
                    No rows
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', borderTop: `1px solid ${colors.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: colors.subText }}>
            {total === 0 ? '0 results' : `Showing ${(pageSafe - 1) * pageSize + 1}-${Math.min(pageSafe * pageSize, total)} of ${total}`}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" style={buttonStyle('neutral', pageSafe <= 1)} disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </button>
            <div style={{ fontSize: 12, fontWeight: 900, color: colors.text }}>
              Page {pageSafe} / {totalPages}
            </div>
            <button
              type="button"
              style={buttonStyle('neutral', pageSafe >= totalPages)}
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Add modal */}
      {isAddOpen ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 50,
          }}
          onMouseDown={() => {
            if (!isAdding) setIsAddOpen(false);
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 640,
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: 14,
              boxSizing: 'border-box',
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: colors.text }}>Add stock history</div>
              <button type="button" style={buttonStyle('neutral', isAdding)} disabled={isAdding} onClick={() => setIsAddOpen(false)}>
                Close
              </button>
            </div>

            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username *</div>
                <input
                  value={(newItem.category ?? '').toString()}
                  onChange={(e) => setNewItem((p) => ({ ...p, userId: 0, username: '', category: e.target.value }))}
                  list="admin-stock-history-add-username-options"
                  placeholder="Select or type..."
                  style={inputStyle}
                />
                <datalist id="admin-stock-history-add-username-options">
                  {usernameOptions.map((u) => (
                    <option key={u} value={u} />
                  ))}
                </datalist>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Stock name *</div>
                <input value={newItem.stockName} onChange={(e) => setNewItem((p) => ({ ...p, stockName: e.target.value }))} style={inputStyle} />
              </div>
            </div>

            {addError ? <div style={{ marginTop: 10, color: isDarkMode ? '#FCA5A5' : '#B91C1C', fontWeight: 900 }}>{addError}</div> : null}

            <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-start' }}>
              <button type="button" style={buttonStyle('success', isAdding)} disabled={isAdding} onClick={submitAdd}>
                {isAdding ? 'Saving…' : 'Save'}
              </button>
              <button type="button" style={buttonStyle('neutral', isAdding)} disabled={isAdding} onClick={() => setIsAddOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminStockHistoryScreen;
